import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowDownload20Regular,
  CalendarLtr24Regular,
  CheckmarkCircle24Filled,
  ContactCard24Regular,
  Location24Regular,
  LockClosed20Regular,
  LockClosed24Regular,
  Mail20Regular,
  Person24Regular,
  QrCode24Regular,
  ShieldCheckmark24Regular,
  Tag24Regular,
} from '@fluentui/react-icons';
import { Reservation } from '@/types/api';
import apiClient from '@/services/api';

/* ------------------------------------------------------------------ */
/*  ReservationCard — the issued ski pass, in the same "receipt"       */
/*  language as the confirmation: reference and status, QR code, the   */
/*  order lines, where the pass went, the PDF.                         */
/* ------------------------------------------------------------------ */

const fmtDate = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const endDate = (iso: string, days: number) => {
  const dt = new Date(`${iso}T00:00:00`);
  dt.setDate(dt.getDate() + Math.max(days, 1) - 1);
  return dt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const eur = (n: number) => n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const STATUS_LABELS: Record<string, string> = {
  pending_payment: 'En attente de paiement',
  paid: 'Payée — validation en cours',
  confirmed: 'Confirmée — forfait émis',
  ready_for_pickup: 'Prête à retirer',
  fulfilled: 'Retirée',
  cancelled: 'Annulée',
  canceled: 'Annulée',
};

// Paid on the AlpiFlow page, in another tab: the card follows the order until it is validated
const POLL_MS = 3000;
const POLL_FOR_MS = 15 * 60 * 1000;

/** The reservation, kept up to date while it waits for its payment on the AlpiFlow page. */
function useLiveReservation(initial: Reservation) {
  const [r, setR] = useState(initial);
  const [following, setFollowing] = useState(false);
  const waiting = Boolean(r.payment_url);
  const busy = useRef(false);

  useEffect(() => setR(initial), [initial]);

  const check = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      setR(await apiClient.getReservation(initial.reservation_id));
    } catch {
      // Network hiccup: the next check will do
    } finally {
      busy.current = false;
    }
  }, [initial.reservation_id]);

  // Once on display, and whenever the customer comes back to this tab
  useEffect(() => {
    if (!waiting) return;
    void check();
    const onFocus = () => { if (document.visibilityState === 'visible') void check(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [waiting, check]);

  // After a click on "Payer": every 3 s, for 15 minutes at most
  useEffect(() => {
    if (!waiting || !following) return;
    const timer = window.setInterval(() => void check(), POLL_MS);
    const stop = window.setTimeout(() => setFollowing(false), POLL_FOR_MS);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [waiting, following, check]);

  return { r, follow: () => setFollowing(true) };
}

const ReservationCard: React.FC<{ reservation: Reservation }> = ({ reservation }) => {
  const { r, follow } = useLiveReservation(reservation);
  const multiDay = r.valid_days > 1;
  const total = r.total_eur ?? r.price_eur;
  const paid = r.payment?.status === 'paid';
  // Same site as the chat: the absolute link is for MCP clients that only get text
  const payUrl = r.payment_url ? new URL(r.payment_url, window.location.origin).pathname : null;
  const code = r.codes?.find((c) => c.code)?.code;
  const lines: { icon: React.ReactNode; label: string; value: string }[] = [
    { icon: <Tag24Regular />, label: 'Forfait', value: r.pass_label },
    {
      icon: <CalendarLtr24Regular />,
      label: 'Validité',
      value: multiDay ? `du ${fmtDate(r.start_date)} au ${endDate(r.start_date, r.valid_days)}` : fmtDate(r.start_date),
    },
    { icon: <Person24Regular />, label: 'Skieur', value: r.holder_name },
    {
      icon: <ContactCard24Regular />,
      label: 'Carte support',
      value: r.keycard_number ? `carte existante n° ${r.keycard_number}` : r.new_card ? 'nouvelle carte' : '—',
    },
    { icon: <ShieldCheckmark24Regular />, label: 'Assurance', value: r.insurance ? "Assur'Glisse" : 'sans assurance' },
    {
      icon: <LockClosed24Regular />,
      label: 'Paiement',
      value: paid
        ? `${r.payment?.brand ?? 'Carte'} •••• ${r.payment?.last4 ?? '••••'}${
            r.payment?.provider === 'alpiflow' ? ' (AlpiFlow, mode test)' : r.payment?.simulated ? ' (démonstration)' : ''}`
        : payUrl
          ? 'en attente — par carte sur la page de paiement sécurisée'
          : 'non réglé — à payer avant le retrait',
    },
  ];
  if (r.withdrawal_label) lines.push({ icon: <Location24Regular />, label: 'Retrait de la carte', value: r.withdrawal_label });
  if (code) lines.push({ icon: <QrCode24Regular />, label: 'Code de retrait', value: code });

  return (
    <div className="receipt receipt-reservation">
      <div className={`receipt-head${paid ? ' receipt-head-success' : ''}`}>
        <div className="receipt-kicker">
          <CheckmarkCircle24Filled /> {STATUS_LABELS[r.status] ?? 'Réservation enregistrée'}
        </div>
        <div className="receipt-ref">{r.reservation_id}</div>
        <div className="receipt-total">
          <span className="receipt-amount">{eur(total)}</span>
          <span className="receipt-note">
            {r.extras_eur ? `forfait ${eur(r.price_eur)} + options ${eur(r.extras_eur)} · ` : ''}
            {paid ? 'payé' : payUrl ? 'à payer' : 'à régler ultérieurement'}
          </span>
        </div>
      </div>

      <div className="receipt-body receipt-grid">
        <ul className="receipt-lines">
          {lines.map((l) => (
            <li key={l.label} className="receipt-line">
              <span className="receipt-icon">{l.icon}</span>
              <span className="receipt-text">
                <span className="receipt-label">{l.label}</span>
                <span className="receipt-value">{l.value}</span>
              </span>
            </li>
          ))}
        </ul>
        {r.qr_svg && !payUrl && (
          <div className="receipt-qr" aria-label="QR code de la réservation">
            {/* SVG produced server-side by segno from the reservation payload */}
            <div dangerouslySetInnerHTML={{ __html: r.qr_svg }} />
            <span>{code ? `Code ${code} — à présenter au retrait` : 'À présenter au retrait'}</span>
          </div>
        )}
      </div>

      {payUrl && (
        <div className="receipt-actions receipt-pay">
          <span className="receipt-pay-note">
            Paiement sur la page sécurisée AlpiFlow Marketplace, dans un nouvel onglet. Cette carte affichera
            votre QR code dès le paiement accepté.
          </span>
          <a className="of-btn of-btn-primary" href={payUrl} target="_blank" rel="noopener noreferrer" onClick={follow}>
            <LockClosed20Regular /> Payer {eur(total)}
          </a>
        </div>
      )}

      <div className="receipt-actions receipt-actions-split">
        <span className="receipt-delivery">
          <Mail20Regular />
          {payUrl
            ? `Le forfait sera envoyé à ${r.email} dès le paiement accepté`
            : r.delivery_channel === 'failed'
              ? `L'envoi à ${r.email} a échoué — téléchargez le PDF.`
              : `Récapitulatif envoyé à ${r.email}`}
        </span>
        {r.pdf_url && !payUrl && (
          <a className="of-btn of-btn-primary" href={r.pdf_url} target="_blank" rel="noreferrer">
            <ArrowDownload20Regular /> Télécharger le pass (PDF)
          </a>
        )}
      </div>
    </div>
  );
};

export default ReservationCard;
